import React, { useState } from 'react';
import { Info, ArrowRight, CheckSquare, Square, CaretRight, Warning, Lightbulb } from '@phosphor-icons/react';

/**
 * In-App Guidance: 1-2 line contextual help for each module
 */
export const ModuleGuide = ({ text, className = '' }) => (
  <div className={`flex items-start gap-2.5 bg-sky-50 border border-sky-200 px-4 py-2.5 mb-5 ${className}`} data-testid="module-guide">
    <Info size={16} weight="fill" className="text-sky-600 flex-shrink-0 mt-0.5" />
    <p className="text-xs text-sky-800 leading-relaxed">{text}</p>
  </div>
);

/**
 * Empty State: Helpful messages when no data exists
 */
export const EmptyState = ({ icon: Icon, title, description, actionLabel, onAction, testId = 'empty-state' }) => (
  <div className="bg-white border border-zinc-200 p-10 text-center" data-testid={testId}>
    {Icon && <Icon size={40} className="mx-auto text-zinc-300 mb-3" />}
    <p className="text-sm font-medium text-zinc-700">{title}</p>
    {description && <p className="text-xs text-zinc-500 mt-1.5 max-w-sm mx-auto leading-relaxed">{description}</p>}
    {actionLabel && onAction && (
      <button onClick={onAction}
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 transition-colors"
        data-testid={`${testId}-action`}>
        {actionLabel} <ArrowRight size={12} />
      </button>
    )}
  </div>
);

/**
 * Workflow Progress: Visual step indicator for project lifecycle
 */
const STEPS = [
  { key: 'Draft', label: 'Draft' },
  { key: 'Interim Approval', label: 'Interim Approval' },
  { key: 'Confirmed', label: 'Confirmed' },
  { key: 'Execution', label: 'Execution' },
];

const TERMINAL = ['On Hold', 'Client Not Reverting', 'Cancelled'];

const stepIndex = (status) => {
  if (status === 'Confirmed') return 2;
  if (status === 'Interim Approval') return 1;
  if (status === 'Draft') return 0;
  return -1;
};

export const WorkflowProgress = ({ status, executionAllowed, className = '' }) => {
  const isTerminal = TERMINAL.includes(status);
  const currentIdx = isTerminal ? -1 : stepIndex(status);
  const executionActive = status === 'Confirmed' && executionAllowed;

  return (
    <div className={`bg-white border border-zinc-200 p-4 ${className}`} data-testid="workflow-progress">
      {isTerminal ? (
        <div className="flex items-center gap-2">
          <Warning size={16} weight="fill" className="text-orange-500" />
          <span className="text-xs font-semibold text-orange-700">Project is currently: {status}</span>
        </div>
      ) : (
        <div className="flex items-center gap-0">
          {STEPS.map((step, idx) => {
            const isActive = idx === currentIdx || (idx === 3 && executionActive);
            const isPast = idx < currentIdx || (idx < 3 && executionActive && idx <= currentIdx);
            const isCurrent = idx === currentIdx && !(idx === 2 && executionActive);
            const isExec = idx === 3;

            return (
              <React.Fragment key={step.key}>
                {idx > 0 && (
                  <div className={`flex-1 h-0.5 ${isPast ? 'bg-green-500' : 'bg-zinc-200'}`} />
                )}
                <div className="flex flex-col items-center relative" style={{ minWidth: idx === 3 ? '80px' : '90px' }}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all ${
                    isActive || isPast ? 'bg-green-500 border-green-500 text-white' : isCurrent ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-zinc-300 text-zinc-400'
                  }`}>
                    {isPast && !isCurrent ? '✓' : idx + 1}
                  </div>
                  <span className={`text-[10px] mt-1 text-center leading-tight ${
                    isActive || isCurrent ? 'font-bold text-zinc-900' : isPast ? 'font-medium text-green-700' : 'text-zinc-400'
                  }`}>
                    {step.label}
                  </span>
                  {isCurrent && !isExec && (
                    <span className="absolute -bottom-4 text-[9px] font-bold text-blue-600 whitespace-nowrap">Current</span>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Next Step Hint: Contextual "what to do next" guidance
 */
export const NextStepHint = ({ text, className = '' }) => (
  <div className={`flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-2 ${className}`} data-testid="next-step-hint">
    <Lightbulb size={14} weight="fill" className="text-amber-600 flex-shrink-0" />
    <span className="text-xs text-amber-800 font-medium">Next: {text}</span>
  </div>
);

/**
 * Light Checklist: Optional pre-action verification
 */
export const LightChecklist = ({ title, items, className = '' }) => {
  const [checked, setChecked] = useState(items.map(() => false));
  const toggle = (idx) => setChecked(prev => prev.map((v, i) => i === idx ? !v : v));
  const allChecked = checked.every(Boolean);

  return (
    <div className={`bg-zinc-50 border border-zinc-200 p-3 ${className}`} data-testid="light-checklist">
      <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">{title}</p>
      <div className="space-y-1.5">
        {items.map((item, idx) => (
          <button key={idx} onClick={() => toggle(idx)}
            className="flex items-center gap-2 text-xs text-zinc-700 hover:text-zinc-900 w-full text-left transition-colors"
            data-testid={`checklist-item-${idx}`}>
            {checked[idx]
              ? <CheckSquare size={16} weight="fill" className="text-green-600 flex-shrink-0" />
              : <Square size={16} className="text-zinc-400 flex-shrink-0" />
            }
            <span className={checked[idx] ? 'line-through text-zinc-400' : ''}>{item}</span>
          </button>
        ))}
      </div>
      {allChecked && (
        <p className="text-[10px] text-green-700 font-semibold mt-2 flex items-center gap-1">
          <CaretRight size={10} weight="bold" /> All items verified — proceed
        </p>
      )}
    </div>
  );
};

/**
 * Status explanation mapping for blocked action toasts
 */
export const BLOCK_MESSAGES = {
  po_pending: 'Execution is blocked because Client PO has not been submitted. Upload the Client PO document or request a Super Admin override.',
  project_not_confirmed: 'This feature is available only for Confirmed projects. Update the project status to Confirmed first.',
  permission_denied: 'You do not have permission to perform this action. Contact your Admin if you need access.',
  boq_locked: 'This BOQ is locked after confirmation. You can only view, export, or add Variation Orders.',
};
